import React, { useEffect, useMemo } from 'react';
import { useDigitalTwinNetworkData } from './useDigitalTwinNetworkData';
import { useDigitalTwinNetworkLayers } from './useDigitalTwinNetworkLayers';
import type { ViewState } from './useMapViewState';
import type { ResumeDigitalTwin } from '@/domain/DigitalTwin/DigitalTwin';

interface UseDigitalTwinNetworkOverlayParams {
	enabled: boolean;
	selectedDigitalTwinId: string | null;
	selectedDigitalTwinsData: ResumeDigitalTwin[];
	setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
}

export function useDigitalTwinNetworkOverlay({
	enabled,
	selectedDigitalTwinId,
	selectedDigitalTwinsData,
	setViewState,
}: UseDigitalTwinNetworkOverlayParams) {
	const selectedDTForNetwork = useMemo(
		() => selectedDigitalTwinsData?.find((dt) => dt.inpId?.toString() === selectedDigitalTwinId),
		[selectedDigitalTwinsData, selectedDigitalTwinId],
	);

	const isEnabled = enabled && !!selectedDigitalTwinId;

	const dtNetworkData = useDigitalTwinNetworkData({
		inpId: selectedDTForNetwork?.inpId ?? null,
		enabled: isEnabled,
	});

	const dtNetworkLayers = useDigitalTwinNetworkLayers({
		nodes: dtNetworkData.nodes,
		lines: dtNetworkData.lines,
		enabled: isEnabled,
	});

	useEffect(() => {
		if (enabled && selectedDigitalTwinId && dtNetworkData.dataCenter) {
			const { longitude, latitude } = dtNetworkData.dataCenter;
			if (longitude && latitude) {
				setViewState((prev) => ({ ...prev, longitude, latitude, zoom: 14 }));
			}
		}
	}, [enabled, selectedDigitalTwinId, dtNetworkData.dataCenter]);

	return { dtNetworkLayers };
}
